import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { BookOpen, GraduationCap, FileText, MessageSquare, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function WelcomeActionScreen({ onContinue }: { onContinue: () => void }) {
  const { role, globalRole, userIntent } = useAuth();
  const navigate = useNavigate();

  const isTutor = role === 'tutor' || globalRole === 'tutor';
  const isProfessional = userIntent === 'professional';
  const canCreateSources = isTutor || isProfessional;

  const handleAction = (path: string) => {
    onContinue();
    navigate(path);
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] p-4 text-center bg-bg-main relative z-50">
      <div className="bg-bg-alt border border-border-main p-6 sm:p-10 rounded-3xl max-w-5xl w-full shadow-2xl relative overflow-hidden">
        <div className="max-w-3xl mx-auto mb-8">
          <p className="text-sm font-bold tracking-widest uppercase text-primary mb-3">
            Start with trusted sources
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-text-main mb-4">
            What would you like to do in LuminaLearn?
          </h2>
          <p className="text-text-alt text-base sm:text-lg">
            Create a Source Set, ask questions from your materials, create reviewable learning, or continue studying.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 text-left">
          {canCreateSources && (
            <button
              onClick={() => handleAction('/source-sets')}
              className="flex flex-col items-start p-6 bg-bg-main border border-primary/40 rounded-2xl hover:border-primary hover:shadow-lg transition-all group text-left"
            >
              <div className="h-12 w-12 bg-primary/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <FileText className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-bold text-text-main text-xl mb-2">Open Source Sets</h3>
              <p className="text-sm text-text-alt flex-1">
                Group documents and URLs into one trusted knowledge package for summaries, Ask Source, and Create Learning.
              </p>
              <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-primary">
                Start here <ArrowRight className="h-4 w-4" />
              </span>
            </button>
          )}

          {canCreateSources && (
            <button
              onClick={() => handleAction('/source-sets')}
              className="flex flex-col items-start p-6 bg-bg-main border border-border-main rounded-2xl hover:border-emerald-500 hover:shadow-lg transition-all group text-left"
            >
              <div className="h-12 w-12 bg-emerald-500/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <MessageSquare className="h-6 w-6 text-emerald-500" />
              </div>
              <h3 className="font-bold text-text-main text-xl mb-2">Ask or Create Learning</h3>
              <p className="text-sm text-text-alt flex-1">
                Open a Source Set to ask grounded questions, generate a summary, or create a course draft for review.
              </p>
              <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-emerald-500">
                Use your sources <ArrowRight className="h-4 w-4" />
              </span>
            </button>
          )}

          <button
            onClick={() => handleAction(isTutor ? '/dashboard' : '/')}
            className="flex flex-col items-start p-6 bg-bg-main border border-border-main rounded-2xl hover:border-amber-500 hover:shadow-lg transition-all group text-left"
          >
            <div className="h-12 w-12 bg-amber-500/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <GraduationCap className="h-6 w-6 text-amber-500" />
            </div>
            <h3 className="font-bold text-text-main text-xl mb-2">Continue Learning</h3>
            <p className="text-sm text-text-alt flex-1">
              Continue a course, browse available learning, or return to your dashboard.
            </p>
            <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-amber-500">
              Continue <ArrowRight className="h-4 w-4" />
            </span>
          </button>
        </div>

        {isTutor && (
          <div className="mt-6 rounded-2xl border border-border-main bg-bg-main/60 p-4 text-left">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h3 className="font-bold text-text-main flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-text-alt" />
                  Need the old single-document flow?
                </h3>
                <p className="text-sm text-text-alt mt-1">
                  Classic Course Creator remains available as a fallback for simple course creation.
                </p>
              </div>
              <button
                onClick={() => handleAction('/courses/manage')}
                className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-border-main bg-bg-alt text-text-main font-bold hover:bg-bg-main transition-colors"
              >
                Open Classic Creator
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        <div className="mt-8 text-center text-text-alt">
          <button
            onClick={() => handleAction(isTutor ? '/dashboard' : '/')}
            className="text-sm font-medium hover:text-text-main hover:underline"
          >
            Skip this and go to {isTutor ? 'Dashboard' : 'Course Library'}
          </button>
        </div>
      </div>
    </div>
  );
}
